import React, { ReactNode, useEffect, useState } from 'react';
import Form from '../../../components/form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { can, route } from '../../../services/utils';
import {
  EQUIPMENT_LIST_INDEX,
  EQUIPMENT_LIST_EDIT,
} from '../../../router/routes';
import Link from '../../../components/form/elements/link';
import {
  createNewFormValues,
  getEquipmentFormBodyElementsView,
  getEquipmentSchema,
  getEquipmentTypeText,
} from './logic';
import { useService } from '../../../services/services';
import Readonly from '../../../components/form/elements/readonly';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import { AllEquipmentFormFields } from '../../../interfaces/equipment';
import { EquipmentType } from '../../../enums/equipment-type';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

type EquipmentViewProps = {
  children?: ReactNode;
};

export default function EquipmentView({ children }: EquipmentViewProps) {
  const { t } = useTranslation();
  const history = useHistory();

  // get URL param and initialize default tab based on it
  const { farmId, equipmentType, id } = useParams<{
    farmId: string;
    equipmentType: string;
    id: string;
  }>();

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const equipmentTypeEnum =
    equipmentType as keyof typeof EquipmentType as EquipmentType;

  // for equipment data
  const { classifierService, equipmentService } = useService();
  const [formValues, setFormValues] = useState(
    createNewFormValues(equipmentType)
  );
  useEffect(() => {
    const subscription = equipmentService
      .show(farmId, id)
      .subscribe((newFormValues: AllEquipmentFormFields) => {
        const equipmentTypeText = getEquipmentTypeText(
          t,
          newFormValues.equipment_type
        ); // parse to string directly
        setFormValues({
          ...newFormValues,
          equipment_type_text: equipmentTypeText,
        });
      });
    return () => subscription.unsubscribe();
  }, [id, equipmentService, farmId, t]);

  // for width and measurement checks in form
  const [needsWidthAndMeasurement, setNeedsWidthAndMeasurement] =
    useState(false);
  useEffect(() => {
    if (formValues.type) {
      const properties = formValues.type.properties;
      const newNeedsWidthAndMeasurement = Object.values(properties).some(
        (value) => {
          const isWidthProperty =
            value.property.key === ClassifierPropertyKey.WIDTH_AND_UNIT;
          const isRequired = value.value;
          return isWidthProperty && isRequired;
        }
      );
      if (needsWidthAndMeasurement !== newNeedsWidthAndMeasurement) {
        setNeedsWidthAndMeasurement(newNeedsWidthAndMeasurement);
      }
    } else if (needsWidthAndMeasurement) {
      setNeedsWidthAndMeasurement(false);
    }
  }, [formValues.type, needsWidthAndMeasurement]);

  // for validations, when the chosen equipment type changes, so do the required fields
  const [equipmentSchema, setEquipmentSchema] = useState(
    getEquipmentSchema(equipmentService, formValues.equipment_type, null)
  );
  useEffect(() => {
    setEquipmentSchema(
      getEquipmentSchema(
        equipmentService,
        formValues.equipment_type,
        needsWidthAndMeasurement
      )
    );
  }, [formValues, needsWidthAndMeasurement, equipmentService]);

  // form structure
  const [bodyElements, setBodyElements] = useState([
    Readonly(
      getEquipmentFormBodyElementsView(
        farmId,
        t,
        equipmentTypeEnum,
        needsWidthAndMeasurement,
        formValues,
        setFormValues,
        setNeedsWidthAndMeasurement,
        classifierService,
        equipmentService,
        history
      )
    ),
  ]);
  useEffect(() => {
    setBodyElements([
      Readonly(
        getEquipmentFormBodyElementsView(
          farmId,
          t,
          equipmentTypeEnum,
          needsWidthAndMeasurement,
          formValues,
          setFormValues,
          setNeedsWidthAndMeasurement,
          classifierService,
          equipmentService,
          history
        )
      ),
    ]);
  }, [
    formValues,
    needsWidthAndMeasurement,
    classifierService,
    equipmentService,
    farmId,
    history,
    t,
    equipmentTypeEnum,
  ]);

  // the footer for navigating back
  let backRoute = route(EQUIPMENT_LIST_INDEX, {
    farmId: farmId,
    equipmentType: equipmentType, // this will return to the tab that corresponds to the chosen type
  });
  const [footerElements] = useState(() => {
    let elements = [];

    if (can(farmUser, { title: 'update equipment' })) {
      elements.push(
        Link(
          route(EQUIPMENT_LIST_EDIT, { farmId, equipmentType, id }),
          'edit',
          true
        )
      );
    }

    return elements;
  });

  // in view and edit mode, we attempt to create the form titles dynamically
  const formTitle =
    formValues.label !== null ? formValues.label : t('pages.equipment.view'); // fallback in case values not filled out yet

  return (
    <Form
      mode={'view'}
      title={formTitle}
      backTo={backRoute}
      body={bodyElements}
      footer={footerElements}
      values={formValues}
      submit={null}
      schema={equipmentSchema}
      noticeAccessor="ViewForm"
    />
  );
}
